import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { setAppInsightsUser } from '@/lib/applicationInsights';
import {
  type AuthSessionUser,
  authFetch,
  responseDetail,
  subscribeToAuthSessionExpiry,
} from '@/lib/auth/session';

interface AuthContextValue {
  user: AuthSessionUser | null;
  isLoading: boolean;
  error: string | null;
  setError: (message: string | null) => void;
  clearError: () => void;
  refresh: () => Promise<AuthSessionUser | null>;
  login: (returnTo?: string) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function fetchSession(): Promise<AuthSessionUser | null> {
  const response = await authFetch('/api/auth/me', {
    headers: { Accept: 'application/json' },
  });

  if (response.status === 401) return null;

  if (!response.ok) {
    throw new Error(await responseDetail(response, 'Failed to load auth session'));
  }

  const data = await response.json();
  return data.email ? data : null;
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthSessionUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const applySession = useCallback((sessionUser: AuthSessionUser | null) => {
    setUser(sessionUser);
    setAppInsightsUser(sessionUser?.email);
    if (sessionUser?.email) {
      setError(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    const sessionUser = await fetchSession();
    applySession(sessionUser);
    return sessionUser;
  }, [applySession]);

  useEffect(
    () =>
      subscribeToAuthSessionExpiry(() => {
        setError(null);
        applySession(null);
      }),
    [applySession]
  );

  useEffect(() => {
    let mounted = true;

    fetchSession()
      .then((sessionUser) => {
        if (mounted) applySession(sessionUser);
      })
      .catch((err) => {
        if (!mounted) return;
        setUser(null);
        setAppInsightsUser(undefined);
        setError(err instanceof Error ? err.message : 'Failed to load auth session');
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [applySession]);

  const login = useCallback((returnTo?: string) => {
    const target = returnTo || `${window.location.pathname}${window.location.search}`;
    window.location.href = `/api/auth/login?return_to=${encodeURIComponent(target)}`;
  }, []);

  const logout = useCallback(async () => {
    const response = await authFetch('/api/auth/logout', { method: 'POST' });

    if (!response.ok) {
      const detail = await responseDetail(response, 'Failed to sign out');
      toast.error(detail);
      throw new Error(detail);
    }

    const data = await response.json().catch(() => null);
    const logoutUrl = typeof data?.logoutUrl === 'string' ? data.logoutUrl : '/auth/login';
    // Don't clear `user` here. This is a full-page navigation, so React state is
    // discarded on load anyway — but flipping `user` to null first makes
    // AccountLayout's "no user -> redirect to login" effect fire and race this
    // redirect, re-authenticating via the still-live SSO session and bouncing the
    // user back to /home instead of landing on the login screen.
    setAppInsightsUser(undefined);
    window.location.href = logoutUrl;
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    isLoading,
    error,
    setError,
    clearError,
    refresh,
    login,
    logout,
  }), [user, isLoading, error, clearError, refresh, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
